import { RawDraftContentState } from 'draft-js';
import { flatten, flattenDeep, isNull, set } from 'lodash/fp';

import { ApplicationState } from '../../../../store/types';
import { sortPlaybookShelves, ABSTRACT_ID } from '../../../constants/playbooks';
import { getAvailableDocumentNames } from '../../documents/store/selectors';
import { PlaybookPage, PlaybookBuilderTab, Playbook, PlaybookText, PlaybookView, TagContent, PlaybookDB, BasicPlaybook, PlaybookHistory, BuildPage, PlaybookQueryDB, PlaybookMessage, SuggestedChangesDB, DraggableTile, OpenSuggestedChange, PlaybookPageView, PlaybookProvision, ReadOnlyContentType, PlaybookSmartSearch, AvailablePlaybookProvisionLinks, LinkedPlaybookProvision, UpdatedProvisionLinks, SystemAdminBookshelfView } from './types';
import { isSubheaderSection } from './typeAssertions';
import { getPlaybookRoot } from '../../store/selectors';

const getTextFromEditor = (content: RawDraftContentState | null) => isNull(content) ? [''] : content.blocks.map(({ text }) => text);

export const getPlaybookPage = (state: ApplicationState): PlaybookPage => getPlaybookRoot(state).playbookPage;

export const getBuildPage = (state: ApplicationState): BuildPage => getPlaybookRoot(state).buildPage;

export const getSelectedTab = (state: ApplicationState): PlaybookBuilderTab => getPlaybookRoot(state).selectedTab;

export const getPlaybook = (state: ApplicationState): Playbook => getPlaybookRoot(state).playbook;

export const getPlaybookHistory = (state: ApplicationState): PlaybookHistory[] => getPlaybookRoot(state).playbookHistory;

export const getOpenSections = (state: ApplicationState): string[] => getPlaybookRoot(state).openSections;

export const getOpenDeviations = (state: ApplicationState): string[] => getPlaybookRoot(state).openDeviations;

export const getCurrentView = (state: ApplicationState): PlaybookView => getPlaybookRoot(state).currentView;

export const getTagToCreate = (state: ApplicationState): TagContent | null => getPlaybookRoot(state).tagToCreate;

export const getCreateTagModalOpen = (state: ApplicationState): boolean => !isNull(getTagToCreate(state));

export const getPlaybookText = (state: ApplicationState): PlaybookText => {
    const abstract = getPlaybook(state).content.abstract;
    const abstractText = getTextFromEditor(abstract);
    const provisions = getPlaybook(state).content.sections.filter(section => !isSubheaderSection(section)) as PlaybookProvision[];
    const playbookText = provisions.reduce((acc: PlaybookText, { sectionId, provisionContent, backgroundContent, preferredTerms, deviations }: PlaybookProvision) => {
        return set(sectionId, [...getTextFromEditor(provisionContent), ...getTextFromEditor(backgroundContent), ...getTextFromEditor(preferredTerms), ...deviations.map(({ topic }) => topic), ...flatten(deviations.map(({ variations }) => ([...getTextFromEditor(variations)]))), ...flatten(deviations.map(({ background }) => ([...getTextFromEditor(background)]))), ...deviations.map(({ nid }) => nid)], acc);
    }, { [ABSTRACT_ID]: abstractText }
    );
    return playbookText;
};

export const getPlaybookPublishReason = (state: ApplicationState): string => getPlaybookRoot(state).publishReason;

export const getPlaybookPublishError = (state: ApplicationState): string | null => getPlaybookRoot(state).publishError;

export const getAllPlaybooks = (state: ApplicationState): BasicPlaybook[] => getPlaybookRoot(state).allPlaybooks;

export const getAllTemplates = (state: ApplicationState): BasicPlaybook[] => getPlaybookRoot(state).allTemplates;

export const getCurrentPlaybook = (state: ApplicationState): PlaybookDB | null => getPlaybookRoot(state).currentPlaybook;

export const getPlaybookIsDraft = (state: ApplicationState): boolean => !!getPlaybookRoot(state).playbook.isDraft;

export const getPlaybookCanSave = (state: ApplicationState): boolean => getPlaybookRoot(state).canSave;

export const getPlaybookPublishModalOpen = (state: ApplicationState): boolean => getPlaybookRoot(state).publishModalOpen;

export const getPlaybookSaveModalOpen = (state: ApplicationState): boolean => getPlaybookRoot(state).saveModalOpen;

export const getPlaybookCanNext = (state: ApplicationState): boolean => getPlaybookRoot(state).canNext;

export const getPlaybookCanPublish = (state: ApplicationState): boolean => getPlaybookRoot(state).canPublish;

export const getPlaybookVersionUpdated = (state: ApplicationState): boolean => getPlaybookRoot(state).versionUpdated;

export const getPlaybookVersionMajorIncreased = (state: ApplicationState): boolean => getPlaybookRoot(state).versionMajorIncreased;

export const getPlaybookShelves = (state: ApplicationState) => {
    const searchTerm = getPlaybookSimpleSearchTerm(state);
    const systemAdminBookshelfView = getSystemAdminBookshelfView(state);
    let allPlaybooks: BasicPlaybook[] = getAllPlaybooks(state);
    if (searchTerm !== '') {
        const allDocumentNames = getAvailableDocumentNames(state);
        const getDocumentNames = (ids: string[] | null) => ids ? allDocumentNames.reduce((acc: string[], { documentNameId, documentName }) => documentNameId && ids.includes(documentNameId.toString()) ? [...acc, documentName.toLowerCase()] : acc, []) : [];
        const playbooksToSearch = allPlaybooks.map(({ playbookId, agreementType, createdBy, name, documentNameIds, versionMajor, versionMinor, isDraft }) => ({ playbookId: playbookId, searchableValues: flattenDeep([!isNull(agreementType) ? agreementType.toString().toLowerCase() : '', createdBy.toLowerCase(), name.toLowerCase(), getDocumentNames(documentNameIds), versionMajor.toString(), `${versionMajor.toString()}.${versionMinor.toString()}`, isDraft ? 'draft' : '']) }));
        const matchedPlaybookIds = playbooksToSearch.map(({ playbookId, searchableValues }) => searchableValues.some(text => text.includes(searchTerm)) ? playbookId : null).filter(value => !isNull(value));
        allPlaybooks = allPlaybooks.filter(playbook => matchedPlaybookIds.includes(playbook.playbookId));
    }
    if (systemAdminBookshelfView !== SystemAdminBookshelfView.ALL) {
        const templatesOnly = systemAdminBookshelfView === SystemAdminBookshelfView.TEMPLATES;
        allPlaybooks = templatesOnly ? allPlaybooks.filter(({ isSystemTemplate }) => !!isSystemTemplate) : allPlaybooks.filter(({ isSystemTemplate }) => !isSystemTemplate);
    }
    const playbookShelves = sortPlaybookShelves(allPlaybooks);
    return playbookShelves;
};

export const getPlaybookTemplateShelves = (state: ApplicationState) => {
    const searchTerm = getPlaybookTemplateSimpleSearchTerm(state);
    let allTemplates: BasicPlaybook[] = getAllTemplates(state);
    if (searchTerm !== '') {
        const allDocumentNames = getAvailableDocumentNames(state);
        const getDocumentNames = (ids: string[] | null) => ids ? allDocumentNames.reduce((acc: string[], { documentNameId, documentName }) => documentNameId && ids.includes(documentNameId.toString()) ? [...acc, documentName.toLowerCase()] : acc, []) : [];
        const playbooksToSearch = allTemplates.map(({ playbookId, agreementType, createdBy, name, documentNameIds, versionMajor, versionMinor, isDraft }) => ({ playbookId: playbookId, searchableValues: flattenDeep([!isNull(agreementType) ? agreementType.toString().toLowerCase() : '', createdBy.toLowerCase(), name.toLowerCase(), getDocumentNames(documentNameIds), versionMajor.toString(), `${versionMajor.toString()}.${versionMinor.toString()}`, isDraft ? 'draft' : '']) }));
        const matchedPlaybookIds = playbooksToSearch.map(({ playbookId, searchableValues }) => searchableValues.some(text => text.includes(searchTerm)) ? playbookId : null).filter(value => !isNull(value));
        allTemplates = allTemplates.filter(playbook => matchedPlaybookIds.includes(playbook.playbookId));
    }
    const playbookShelves = sortPlaybookShelves(allTemplates);
    return playbookShelves;
};

export const getPlaybookCoverSelected = (state: ApplicationState): BasicPlaybook | null => getPlaybookRoot(state).playbookCoverSelected;

export const getPlaybookCoverModalOpen = (state: ApplicationState): boolean => !isNull(getPlaybookCoverSelected(state));

export const getTemplateCoverSelected = (state: ApplicationState): BasicPlaybook | null => getPlaybookRoot(state).templateCoverSelected;

export const getTemplateCoverModalOpen = (state: ApplicationState): boolean => !isNull(getTemplateCoverSelected(state));

export const getPlaybookQueries = (state: ApplicationState): PlaybookQueryDB[] => getPlaybookRoot(state).queries;

export const getSelectedPlaybookQuery = (state: ApplicationState): PlaybookQueryDB | null => getPlaybookRoot(state).selectedQuery;

export const getQueryModalOpen = (state: ApplicationState): boolean => !isNull(getSelectedPlaybookQuery(state));

export const getQueryResponse = (state: ApplicationState): PlaybookMessage | null => getPlaybookRoot(state).queryResponse;

export const getSendingQueryResponse = (state: ApplicationState): boolean => getPlaybookRoot(state).sendingResponse;

export const getSendEmailReply = (state: ApplicationState): boolean => getPlaybookRoot(state).sendEmailReply;

export const getQueryMarkedAsResolved = (state: ApplicationState): boolean => getPlaybookRoot(state).queryMarkedAsResolved;

export const getPlaybookSuggestedChanges = (state: ApplicationState): SuggestedChangesDB[] => getPlaybookRoot(state).playbookSuggestedChanges;

export const getProvisionOrder = (state: ApplicationState): DraggableTile[] => getPlaybookRoot(state).provisionsOrder;

export const getReorderProvisionModalOpen = (state: ApplicationState): boolean => getPlaybookRoot(state).provisionsModalOpen;

export const getDeviationOrder = (state: ApplicationState): DraggableTile[] => getPlaybookRoot(state).deviationsOrder;

export const getDeviationModalSectionId = (state: ApplicationState): string | null => getPlaybookRoot(state).deviationsModalSectionId;

export const getReorderDeviationModalOpen = (state: ApplicationState): boolean => !isNull(getDeviationModalSectionId(state));

export const getOpenSuggestedChange = (state: ApplicationState): OpenSuggestedChange | null => getPlaybookRoot(state).openSuggestedChange;

export const getCurrentPlaybookPageView = (state: ApplicationState): PlaybookPageView => getPlaybookRoot(state).playbookPageView;

export const getOutstandingSuggestedChanges = (state: ApplicationState): boolean => {
    const suggestedChanges = getPlaybookRoot(state).playbookSuggestedChanges;
    return suggestedChanges.length > 0 && !suggestedChanges.filter(({ outstanding }) => !!outstanding).length;
};

export const getResolvedSuggestedChanges = (state: ApplicationState): number[] => {
    const suggestedChanges = getPlaybookRoot(state).playbookSuggestedChanges;
    return suggestedChanges.filter(({ outstanding }) => !outstanding).map(({ playbookSuggestionId }) => playbookSuggestionId);
};

export const getResolvedChangesExist = (state: ApplicationState): boolean => getResolvedSuggestedChanges(state).length > 0;

export const getSuggestionReviewModalOpen = (state: ApplicationState): boolean => getPlaybookRoot(state).suggestionReviewModalOpen;

export const getSuggestedChangesMessage = (state: ApplicationState): PlaybookMessage | null => getPlaybookRoot(state).suggestedChangesMessage;

export const getSuggestedChangesMessageUpdated = (state: ApplicationState): boolean => getPlaybookRoot(state).suggestedChangesMessageUpdated;

export const getSendingSuggestedChangesMessage = (state: ApplicationState): boolean => getPlaybookRoot(state).sendingMessage;

export const getShowPlaybookViewLoadingSpinner = (state: ApplicationState): boolean => getPlaybookRoot(state).isLoading;

export const getDisablePublishAndVersionControl = (state: ApplicationState): boolean => {
    const currentPlaybook: PlaybookDB | null = getCurrentPlaybook(state);
    const playbookHistory: PlaybookHistory[] = getPlaybookHistory(state);
    const playbookHasDraftVersion = playbookHistory.some(({ isDraft }) => !!isDraft);
    const disablePublishAndVersionControl = playbookHasDraftVersion && !isNull(currentPlaybook) && currentPlaybook.playbookDefinitionId !== playbookHistory.find(({ isDraft }) => !!isDraft)?.playbookDefinitionId;
    return disablePublishAndVersionControl;
};

export const getResolvedPlaybookSuggestedChanges = (state: ApplicationState): SuggestedChangesDB[] => getPlaybookRoot(state).resolvedPlaybookSuggestedChanges;

export const getResolvedPlaybookSuggestedChange = (state: ApplicationState): SuggestedChangesDB | null => getPlaybookRoot(state).resolvedSuggestedChange;

export const getResolvedSuggestedChangesOpen = (state: ApplicationState): boolean => !isNull(getResolvedPlaybookSuggestedChange(state));

export const getResolvedChangesContent = (state: ApplicationState): ReadOnlyContentType => getPlaybookRoot(state).resolvedSuggestedChangesContent;

export const getPlaybookSimpleSearchTerm = (state: ApplicationState): string => getPlaybookRoot(state).simpleSearchTerm;

export const getPlaybookTemplateSimpleSearchTerm = (state: ApplicationState): string => getPlaybookRoot(state).templateSimpleSearchTerm;

export const getPlaybookSmartSearchTerm = (state: ApplicationState): PlaybookSmartSearch[] => getPlaybookRoot(state).smartSearchTerm;

export const getAvailablePlaybookProvisionLinks = (state: ApplicationState): AvailablePlaybookProvisionLinks[] => getPlaybookRoot(state).availableProvisionLinks;

export const getAvailableProvisionLinkModalOpen = (state: ApplicationState): boolean => !isNull(getPlaybookRoot(state).provisionToLink);

export const getProvisionToLink = (state: ApplicationState): string | null => getPlaybookRoot(state).provisionToLink;

export const getCurrentProvisionLinks = (state: ApplicationState): LinkedPlaybookProvision[] => getPlaybookRoot(state).currentProvisionLinks;

export const getLinkedProvisionModalOpen = (state: ApplicationState): boolean => getPlaybookRoot(state).linkedProvisionModalOpen;

export const getProvisionLinksUpdated = (state: ApplicationState): UpdatedProvisionLinks | null => getPlaybookRoot(state).updatedProvisionLinks;

export const getDeleteSectionConfirmationModalOpen = (state: ApplicationState): boolean => !isNull(getPlaybookRoot(state).sectionWithLinksToDelete);

export const getSectionWithLinksToDelete = (state: ApplicationState): string | null => getPlaybookRoot(state).sectionWithLinksToDelete;

export const getReadOnlyProvisionLinksModalOpen = (state: ApplicationState): string | null => getPlaybookRoot(state).readOnlyLinkedProvisionModalOpen;

export const getLinkedProvisionHistory = (state: ApplicationState): LinkedPlaybookProvision[] => getPlaybookRoot(state).provisionLinkHistory;

export const getLinkedProvisionHistoryModalOpen = (state: ApplicationState): boolean => getPlaybookRoot(state).provisionLinkHistoryModalOpen;

export const getSystemAdminBookshelfView = (state: ApplicationState): SystemAdminBookshelfView => getPlaybookRoot(state).systemAdminBookshelfView;
